'use client';

import { useSearchParams } from 'next/navigation';
import { useState, Suspense, useEffect } from 'react';
import { ChevronLeft, Wrench, Shield, Zap, DollarSign, Clock, X, Calendar, User, Mail, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import Link from 'next/link';
import Head from 'next/head';
import { COMPANY_INFO } from '@/config/company';

function RepairPageContent() {
  const searchParams = useSearchParams();
  const device = searchParams.get('device');
  const brand = searchParams.get('brand');
  const model = searchParams.get('model');
  const [repairType, setRepairType] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    visitDate: '',
    visitTimeSlot: 'allday',
    problemDescription: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);


  const deviceLabels = {
    PHONE: 'Smartphone',
    TABLET: 'Tablette',
    LAPTOP: 'Ordinateur portable',
  };

  const brandLabels = {
    APPLE: 'Apple',
    APPLE_LAPTOP: 'Apple',
    SAMSUNG: 'Samsung',
    GOOGLE: 'Google',
    XIAOMI: 'Xiaomi',
    ONEPLUS: 'OnePlus',
    IPAD: 'iPad',
    DELL: 'Dell',
    HP: 'HP',
    LENOVO: 'Lenovo',
    NINTENDO: 'Nintendo',
    SONY: 'Sony',
    MICROSOFT: 'Microsoft',
  };

  const modelLabels = {
    IPHONE_17_PRO_MAX: 'iPhone 17 Pro Max',
    IPHONE_17_PRO: 'iPhone 17 Pro',
    IPHONE_17: 'iPhone 17',
    IPHONE_16_PRO_MAX: 'iPhone 16 Pro Max',
    IPHONE_16_PRO: 'iPhone 16 Pro',
    IPHONE_16E: 'iPhone 16e',
    IPHONE_16_PLUS: 'iPhone 16 Plus',
    IPHONE_16: 'iPhone 16',
    IPHONE_15_PRO_MAX: 'iPhone 15 Pro Max',
    IPHONE_15_PRO: 'iPhone 15 Pro',
    IPHONE_15_PLUS: 'iPhone 15 Plus',
    IPHONE_15: 'iPhone 15',
    IPHONE_14_PRO_MAX: 'iPhone 14 Pro Max',
    IPHONE_14_PRO: 'iPhone 14 Pro',
    IPHONE_14: 'iPhone 14',
    IPHONE_13_PRO_MAX: 'iPhone 13 Pro Max',
    IPHONE_13_PRO: 'iPhone 13 Pro',
    IPHONE_13: 'iPhone 13',
    IPHONE_13_MINI: 'iPhone 13 Mini',
    IPHONE_12_PRO_MAX: 'iPhone 12 Pro Max',
    IPHONE_12_PRO: 'iPhone 12 Pro',
    IPHONE_12: 'iPhone 12',
    IPHONE_12_MINI: 'iPhone 12 Mini',
    IPHONE_11_PRO_MAX: 'iPhone 11 Pro Max',
    IPHONE_11_PRO: 'iPhone 11 Pro',
    IPHONE_11: 'iPhone 11',
    IPHONE_XS_MAX: 'iPhone XS Max',
    IPHONE_XS: 'iPhone XS',
    IPHONE_XR: 'iPhone XR',
    IPHONE_X: 'iPhone X',
    S23_ULTRA: 'Galaxy S23 Ultra',
    S23: 'Galaxy S23',
    S22: 'Galaxy S22',
    PIXEL_8_PRO: 'Pixel 8 Pro',
    PIXEL_8: 'Pixel 8',
    PIXEL_7_PRO: 'Pixel 7 Pro',
    SWITCH_OLED: 'Switch OLED',
    SWITCH: 'Switch',
    SWITCH_LITE: 'Switch Lite',
    PS5: 'PlayStation 5',
    PS4: 'PlayStation 4',
    XBOX_SERIES_X: 'Xbox Series X',
    XBOX_SERIES_S: 'Xbox Series S',
  };

  // Tarifs spécifiques pour les iPhones
  const iphonePricing = {
    IPHONE_17_PRO_MAX: { screen: '320€', battery: '100€' },
    IPHONE_17_PRO: { screen: '300€', battery: '100€' },
    IPHONE_17: { screen: '280€', battery: '100€' },
    IPHONE_16_PRO_MAX: { screen: '260€', battery: '100€' },
    IPHONE_16_PRO: { screen: '240€', battery: '100€' },
    IPHONE_16E: { screen: '220€', battery: '100€' },
    IPHONE_16_PLUS: { screen: '200€', battery: '100€' },
    IPHONE_16: { screen: '190€', battery: '100€' },
    IPHONE_15_PRO_MAX: { screen: '170€', battery: '100€' },
    IPHONE_15_PRO: { screen: '135€', battery: '80€' },
    IPHONE_15_PLUS: { screen: '145€', battery: '80€' },
    IPHONE_15: { screen: '135€', battery: '80€' },
    IPHONE_14_PRO_MAX: { screen: '135€', battery: '70€' },
    IPHONE_14_PRO: { screen: '110€', battery: '70€' },
    IPHONE_14: { screen: '115€', battery: '70€' },
    IPHONE_13_PRO_MAX: { screen: '130€', battery: '65€' },
    IPHONE_13_PRO: { screen: '110€', battery: '60€' },
    IPHONE_13: { screen: '100€', battery: '60€' },
    IPHONE_13_MINI: { screen: '80€', battery: '60€' },
    IPHONE_12_PRO_MAX: { screen: '100€', battery: '65€' },
    IPHONE_12_PRO: { screen: '80€', battery: '60€' },
    IPHONE_12: { screen: '80€', battery: '60€' },
    IPHONE_12_MINI: { screen: '80€', battery: '60€' },
    IPHONE_11_PRO_MAX: { screen: '90€', battery: '55€' },
    IPHONE_11_PRO: { screen: '75€', battery: '55€' },
    IPHONE_11: { screen: '70€', battery: '55€' },
    IPHONE_XS_MAX: { screen: '75€', battery: '50€' },
    IPHONE_XS: { screen: '65€', battery: '40€' },
    IPHONE_XR: { screen: '60€', battery: '50€' },
    IPHONE_X: { screen: '60€', battery: '50€' },
  };

  // Fonction pour obtenir les prix en fonction du modèle
  const getPricing = (deviceModel) => {
    // Vérifier si c'est un iPhone avec des tarifs spécifiques
    if (iphonePricing[deviceModel]) {
      return iphonePricing[deviceModel];
    }
    // Pour tous les autres appareils, retourner "Sur devis"
    return { screen: 'Sur devis', battery: 'Sur devis' };
  };

  // Obtenir les prix pour le modèle sélectionné
  const pricing = getPricing(model);

  const repairTypes = {
    PHONE: [
      {
        id: 'battery',
        title: 'Batterie',
        description: 'Remplacement de la batterie défaillante ou usée',
        price: pricing.battery,
        time: '15 min',
      },
      {
        id: 'screen',
        title: 'Écran',
        description: 'Remplacement de l\'écran cassé, fissuré ou tâché',
        price: pricing.screen,
        time: '15 min',
      },
      {
        id: 'water',
        title: 'Dégâts des eaux',
        description: 'Diagnostic complet et réparation des dégâts liquides',
        price: 'À partir de 35€',
        time: '15 min - 1h',
      },
      {
        id: 'other',
        title: 'Autre diagnostic',
        description: 'Contactez-nous pour un diagnostic personnalisé',
        price: 'Devis gratuit',
        time: 'À définir',
      },
    ],
    TABLET: [
      {
        id: 'battery',
        title: 'Batterie',
        description: 'Remplacement de la batterie défaillante',
        price: pricing.battery,
        time: '15 min',
      },
      {
        id: 'screen',
        title: 'Écran',
        description: 'Remplacement de l\'écran tactile cassé ou fissuré',
        price: pricing.screen,
        time: '15 min',
      },
      {
        id: 'water',
        title: 'Dégâts des eaux',
        description: 'Diagnostic et réparation des dommages liquides',
        price: 'À partir de 35€',
        time: '15 min - 1h',
      },
      {
        id: 'other',
        title: 'Autre diagnostic',
        description: 'Diagnostic complet et devis personnalisé',
        price: 'Devis gratuit',
        time: 'À définir',
      },
    ],
    LAPTOP: [
      {
        id: 'battery',
        title: 'Batterie',
        description: 'Remplacement de la batterie faible ou défectueuse',
        price: pricing.battery,
        time: '15 min',
      },
      {
        id: 'screen',
        title: 'Écran',
        description: 'Remplacement de l\'écran cassé ou dalle LCD/LED',
        price: pricing.screen,
        time: '15 min',
      },
      {
        id: 'water',
        title: 'Dégâts des eaux',
        description: 'Diagnostic et réparation suite à infiltration liquide',
        price: 'À partir de 35€',
        time: '15 min - 1h',
      },
      {
        id: 'other',
        title: 'Autre diagnostic',
        description: 'Diagnostic complet et devis sur mesure',
        price: 'Devis gratuit',
        time: 'À définir',
      },
    ],
  };

  const availableRepairs = device ? repairTypes[device] : [];

  const handleRepairTypeSelect = (repair) => {
    setRepairType(repair);
    setShowQuoteForm(true);
    setSubmitSuccess(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('/api/repair-quotes/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          deviceType: device,
          deviceBrand: brand,
          deviceModel: model,
          repairType: repairType.id,
          repairTypeLabel: repairType.title,
          estimatedPrice: repairType.price
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setQuoteNumber(data.quote.quoteNumber);
        setFormData({
          customerName: '',
          customerEmail: '',
          customerPhone: '',
          visitDate: '',
          visitTimeSlot: 'allday',
          problemDescription: ''
        });
      } else {
        alert(data.error || 'Erreur lors de la soumission du devis');
      }
    } catch (error) {
      console.error('Submit quote error:', error);
      alert('Erreur lors de la soumission du devis');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    // Si le devis a été créé avec succès et que l'utilisateur n'a pas confirmé
    if (submitSuccess && !confirmClose) {
      // Afficher une alerte de confirmation
      const confirmed = window.confirm(
        '⚠️ ATTENTION : Avez-vous bien noté ou pris en capture d\'écran votre numéro de devis ?\n\n' +
        `Numéro de devis : ${quoteNumber}\n\n` +
        'Vous en aurez besoin lors de votre visite en boutique.'
      );

      if (!confirmed) {
        return; // Ne pas fermer la modale
      }
    }

    setShowQuoteForm(false);
    setSubmitSuccess(false);
    setRepairType(null);
    setConfirmClose(false);
  };

  if (!device || !brand || !model) {
    return (
      <div className="min-h-screen bg-linear-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Paramètres manquants</h1>
          <p className="text-gray-600 mb-6">Veuillez d'abord sélectionner un appareil depuis la page d'accueil.</p>
          <Link href="/">
            <button className="px-6 py-3 bg-[#23994F] text-white rounded-xl font-semibold hover:bg-[#1a7840] transition-colors">
              Retour à l'accueil
            </button>
          </Link>
        </div>
      </div>
    );
  }

  // Mettre à jour le titre de la page dynamiquement
  useEffect(() => {
    if (device && brand && model) {
      const deviceLabel = deviceLabels[device] || device;
      const brandLabel = brandLabels[brand] || brand;
      const modelLabel = modelLabels[model] || model;
      document.title = `Réparation ${deviceLabel} ${brandLabel} ${modelLabel} Saint-Brieuc - Verphone`;
    }
  }, [device, brand, model]);

  // Protection contre la fermeture de la page quand le devis est affiché
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (submitSuccess && showQuoteForm) {
        e.preventDefault();
        e.returnValue = '⚠️ Avez-vous bien noté votre numéro de devis ? Vous en aurez besoin en boutique.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [submitSuccess, showQuoteForm]);

  return (
    <>
      {/* Header */}
      <div className="bg-linear-to-r from-[#23994F] to-[#1a7840] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link href="/">
            <button className="flex items-center gap-2 mb-6 text-white/80 hover:text-white transition-colors">
              <ChevronLeft size={20} />
              <span>Retour</span>
            </button>
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Réparation de votre appareil</h1>
          <div className="flex flex-wrap gap-4 text-white/90 font-semibold">
            <span className="flex items-center gap-2">
              <Wrench size={20} />
              {deviceLabels[device]}
            </span>
            <span>•</span>
            <span>{brandLabels[brand]}</span>
            <span>•</span>
            <span>{modelLabels[model]}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Bonus Écologique Section */}
        <div className="mb-12">
          <div className="relative bg-gradient-to-br from-[#23994F] via-[#1a7840] to-[#166833] rounded-2xl p-6 sm:p-8 overflow-hidden shadow-2xl border-2 border-[#23994F]">
            {/* Motif de fond décoratif */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>

            {/* Contenu */}
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Badge et texte */}
                <div className="flex-1 text-white text-center md:text-left">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                    <Shield size={20} className="text-white" />
                    <span className="text-sm font-bold">Aide de l'État Français</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-bold mb-3 drop-shadow-lg">
                    Bonus Écologique <span className="text-white">-25€</span>
                  </h3>
                  <p className="text-lg sm:text-xl text-white/95 leading-relaxed mb-4">
                    Bénéficiez d'une réduction de <strong className="text-white font-black">25€</strong> sur votre réparation grâce au programme gouvernemental de soutien à la réparation électronique.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                      <CheckCircle size={18} className="text-white" />
                      <span className="text-sm font-semibold">Éco-responsable</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                      <Zap size={18} className="text-white" />
                      <span className="text-sm font-semibold">Économies garanties</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                      <DollarSign size={18} className="text-white" />
                      <span className="text-sm font-semibold">Immédiatement déduit</span>
                    </div>
                  </div>
                </div>

                {/* Badge de réduction visuelle */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-32 h-32 sm:w-40 sm:h-40 bg-white rounded-full flex flex-col items-center justify-center shadow-2xl border-4 border-[#23994F] transform hover:scale-105 transition-transform">
                      <span className="text-5xl sm:text-6xl font-black text-[#23994F]">-25€</span>
                      <span className="text-xs sm:text-sm font-bold text-[#1a7840] mt-1">DE REMISE</span>
                    </div>
                    <div className="absolute -top-2 -right-2 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg animate-bounce-soft border-2 border-yellow-300">
                      <span className="text-2xl">🌱</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Note informative */}
              <div className="mt-6 bg-white/10 backdrop-blur-sm border border-white/30 rounded-xl p-4">
                <p className="text-white/90 text-sm leading-relaxed">
                  <strong className="text-white">Comment ça marche ?</strong> Le bonus écologique est automatiquement appliqué sur les réparations éligibles. Aucune démarche administrative de votre part ! La réduction est directement déduite du montant final de votre réparation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Diagnostic Section */}
        <div className="mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Quel est le problème ?
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Sélectionnez le type de réparation qui correspond à votre besoin
          </p>

          {/* Repair Types Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableRepairs.map((repair, idx) => (
              <div
                key={repair.id}
                className="p-6 rounded-xl border-2 border-gray-200 bg-white hover:border-[#23994F] hover:shadow-xl transition-all duration-300 group stagger-item flex flex-col"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-[#23994F]/10 group-hover:bg-[#23994F]/20 transition-colors">
                    <Wrench size={24} className="text-[#23994F]" />
                  </div>
                  <span className="text-sm font-bold text-[#23994F] bg-[#23994F]/10 px-3 py-1 rounded-full">
                    {repair.price}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#23994F] transition-colors">
                  {repair.title}
                </h3>
                <p className="text-gray-600 mb-4 flex-grow">{repair.description}</p>
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                  <Clock size={16} />
                  <span>{repair.time}</span>
                </div>
                <button
                  onClick={() => handleRepairTypeSelect(repair)}
                  className="w-full bg-[#23994F] hover:bg-[#1a7840] text-white font-semibold px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  Demander un devis
                  <ChevronLeft size={18} className="rotate-180" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-linear-to-r from-[#23994F] to-[#1a7840] rounded-2xl p-8 sm:p-12 text-white text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Besoin d'aide ou devis personnalisé ?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Notre équipe est là pour vous conseiller. Contactez-nous pour un diagnostic complet et un devis sans engagement.
          </p>
          <a
            href={`mailto:${COMPANY_INFO.contact.email}`}
            className="inline-block px-8 py-4 bg-white text-[#23994F] font-bold rounded-xl hover:shadow-2xl hover:shadow-[#23994F]/30 transition-all duration-300 hover:-translate-y-1"
          >
            Contactez-nous par email
          </a>
        </div>
      </div>


      {showQuoteForm && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn"
          onClick={(e) => {
            // Fermer seulement si on clique sur le fond (pas sur la modale)
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full relative shadow-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col animate-slideUp sm:animate-scaleIn">
            <button
              onClick={closeModal}
              className="absolute top-3 sm:top-4 right-3 sm:right-4 p-2 hover:bg-gray-100 rounded-full transition-all z-10 group bg-white shadow-sm"
              aria-label="Fermer"
              title={submitSuccess ? "Attention : avez-vous noté votre numéro de devis ?" : "Fermer"}
            >
              <X size={20} className="text-gray-600 group-hover:text-gray-900 group-hover:rotate-90 transition-transform" />
            </button>

            {submitSuccess ? (
              <div className="p-6 sm:p-8 text-center overflow-y-auto">
                <div className="w-16 h-16 bg-[#23994F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Devis enregistré !
                </h2>
                <p className="text-gray-600 mb-4">
                  Votre demande de devis a été enregistrée avec succès.
                </p>

                {/* Alerte importante */}
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-4">
                  <p className="text-amber-900 font-bold text-sm mb-1 flex items-center justify-center gap-2">
                    <span className="text-xl">⚠️</span>
                    IMPORTANT : Notez bien ce numéro !
                  </p>
                  <p className="text-amber-800 text-xs">
                    Vous en aurez besoin lors de votre passage en boutique
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#23994F]/10 to-[#1a7840]/10 border-2 border-[#23994F] rounded-xl p-5 mb-6 relative">
                  <p className="text-sm text-gray-700 font-semibold mb-2">Votre numéro de devis</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#23994F] font-mono break-all mb-3 select-all">{quoteNumber}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(quoteNumber);
                      alert('Numéro de devis copié dans le presse-papier !');
                    }}
                    className="w-full bg-white hover:bg-gray-50 text-[#23994F] font-semibold px-4 py-2 rounded-lg border-2 border-[#23994F] transition-colors text-sm"
                  >
                    📋 Copier le numéro
                  </button>
                </div>

                <p className="text-gray-700 mb-6 text-sm">
                  Nous vous attendons le <strong>{new Date(formData.visitDate || Date.now()).toLocaleDateString('fr-FR')}</strong> en boutique.
                  Notre technicien aura accès à toutes les informations de votre demande.
                </p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-green-900 font-semibold text-sm mb-2">Conseils :</p>
                  <ul className="text-green-800 text-xs space-y-1 list-disc list-inside">
                    <li>Prenez une capture d'écran de ce numéro</li>
                    <li>Notez-le sur votre téléphone ou un papier</li>
                    <li>Présentez-le lors de votre visite</li>
                  </ul>
                </div>

                <button
                  onClick={closeModal}
                  className="w-full bg-[#23994F] hover:bg-[#1a7840] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  J'ai noté le numéro, fermer
                </button>
              </div>
            ) : (
              <>

                <div className="bg-gradient-to-br from-[#23994F] to-[#1a7840] px-5 sm:px-6 py-5 sm:py-6 text-white flex-shrink-0 rounded-t-2xl sm:rounded-t-2xl">
                  <h2 className="text-lg sm:text-2xl font-bold mb-3 pr-8">
                    Demande de devis gratuit
                  </h2>
                  <div className="flex items-center gap-2.5 bg-white/15 rounded-lg px-3 py-2.5 backdrop-blur border border-white/20">
                    <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Wrench size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm sm:text-base truncate">{repairType?.title}</p>
                      <p className="text-white/80 text-xs">Prix estimé : {repairType?.price}</p>
                    </div>
                  </div>
                </div>


                <div className="overflow-y-auto flex-1 overscroll-contain">
                  <form onSubmit={handleSubmitQuote} className="flex flex-col h-full">
                    <div className="p-4 sm:p-6 space-y-5 flex-1">

                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-6 h-6 bg-[#23994F] rounded-lg flex items-center justify-center text-white text-xs font-bold">1</div>
                        Vos coordonnées
                      </h3>
                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                            Nom complet *
                          </label>
                          <div className="relative">
                            <User size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              name="customerName"
                              value={formData.customerName}
                              onChange={handleFormChange}
                              required
                              className="w-full pl-10 sm:pl-11 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all text-sm"
                              placeholder="Jean Dupont"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                              Email *
                            </label>
                            <div className="relative">
                              <Mail size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="email"
                                name="customerEmail"
                                value={formData.customerEmail}
                                onChange={handleFormChange}
                                required
                                className="w-full pl-10 sm:pl-11 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all text-sm"
                                placeholder="jean@example.com"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                              Téléphone *
                            </label>
                            <div className="relative">
                              <Phone size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="tel"
                                name="customerPhone"
                                value={formData.customerPhone}
                                onChange={handleFormChange}
                                required
                                className="w-full pl-10 sm:pl-11 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all text-sm"
                                placeholder="06 12 34 56 78"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>


                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-6 h-6 bg-[#23994F] rounded-lg flex items-center justify-center text-white text-xs font-bold">2</div>
                        Quand souhaitez-vous passer ?
                      </h3>
                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                            Date de passage *
                          </label>
                          <div className="relative">
                            <Calendar size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="date"
                              name="visitDate"
                              value={formData.visitDate}
                              onChange={handleFormChange}
                              required
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full pl-10 sm:pl-11 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all text-sm"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                            Créneau horaire
                          </label>
                          <div className="relative">
                            <Clock size={18} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <select
                              name="visitTimeSlot"
                              value={formData.visitTimeSlot}
                              onChange={handleFormChange}
                              className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all appearance-none bg-white text-sm sm:text-base"
                            >
                              <option value="allday">Toute la journée (9h-19h)</option>
                              <option value="morning">Matin (9h-12h)</option>
                              <option value="afternoon">Après-midi (14h-19h)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>


                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <div className="w-6 h-6 bg-[#23994F] rounded-lg flex items-center justify-center text-white text-xs font-bold">3</div>
                        Décrivez le problème (optionnel)
                      </h3>
                      <div className="relative">
                        <MessageSquare size={18} className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400" />
                        <textarea
                          name="problemDescription"
                          value={formData.problemDescription}
                          onChange={handleFormChange}
                          rows={4}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] transition-all resize-none text-sm sm:text-base"
                          placeholder="Décrivez le problème de votre appareil..."
                        />
                      </div>
                    </div>


                    <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-4 border border-[#23994F]/20">
                      <h4 className="font-bold text-gray-900 mb-2.5 flex items-center gap-2 text-sm">
                        <Wrench size={16} className="text-[#23994F]" />
                        Récapitulatif
                      </h4>
                      <div className="space-y-2 text-xs sm:text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600">Appareil</span>
                          <span className="font-semibold text-gray-900 text-right">{deviceLabels[device]} {brandLabels[brand]} {modelLabels[model]}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600">Réparation</span>
                          <span className="font-semibold text-gray-900 text-right">{repairType?.title}</span>
                        </div>
                        <div className="flex justify-between gap-2 pt-2 border-t border-gray-300">
                          <span className="text-gray-600">Prix estimé</span>
                          <span className="font-bold text-[#23994F] text-base sm:text-lg">{repairType?.price}</span>
                        </div>
                      </div>
                    </div>
                    </div>


                    <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent border-t border-gray-100 p-4 sm:p-5 flex-shrink-0">
                      <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3">
                        <button
                          type="button"
                          onClick={closeModal}
                          className="sm:flex-1 px-5 py-2.5 sm:py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all text-sm"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={submitting}
                          className="sm:flex-1 px-5 py-3 sm:py-3 bg-gradient-to-r from-[#23994F] to-[#1a7840] hover:from-[#1a7840] hover:to-[#23994F] text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-[#23994F]/20 text-sm"
                        >
                          {submitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function RepairPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#23994F] border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    }>
      <RepairPageContent />
    </Suspense>
  );
}
